import { useState, useEffect } from 'react';
import { Clock, X, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { formatDistanceToNow, parseISO, differenceInHours, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface AutoCloseWarningProps {
  autoCloseAt?: string;
  ticketStatus: string;
  onExtend?: () => void;
  onDismiss?: () => void;
  className?: string;
}

// Default auto-close period in days
const DEFAULT_AUTO_CLOSE_DAYS = 7;

export function AutoCloseWarning({
  autoCloseAt,
  ticketStatus,
  onExtend,
  onDismiss,
  className,
}: AutoCloseWarningProps) {
  const [dismissed, setDismissed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [urgencyLevel, setUrgencyLevel] = useState<'low' | 'medium' | 'high'>('low');
  const [progress, setProgress] = useState(0);

  // Only show for Ready for QA / legacy resolved tickets
  const normalized = String(ticketStatus ?? '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
  const showForStatus =
    normalized === 'ready_for_qa' ||
    normalized === 'fixed' ||
    normalized === 'resolved';
  if (!showForStatus || !autoCloseAt || dismissed) {
    return null;
  }

  useEffect(() => {
    const updateTimeRemaining = () => {
      const closeDate = parseISO(autoCloseAt);
      const now = new Date();
      const hoursRemaining = differenceInHours(closeDate, now);
      const daysRemaining = differenceInDays(closeDate, now);

      // Calculate progress (inverse - how close to closing)
      const totalHours = DEFAULT_AUTO_CLOSE_DAYS * 24;
      const elapsedHours = totalHours - hoursRemaining;
      const progressPercent = Math.min(100, Math.max(0, (elapsedHours / totalHours) * 100));
      setProgress(progressPercent);

      // Set urgency level
      if (hoursRemaining <= 24) {
        setUrgencyLevel('high');
      } else if (daysRemaining <= 2) {
        setUrgencyLevel('medium');
      } else {
        setUrgencyLevel('low');
      }

      // Format time remaining
      setTimeRemaining(formatDistanceToNow(closeDate, { addSuffix: false }));
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [autoCloseAt]);

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const urgencyStyles = {
    low: 'border-muted bg-muted/50',
    medium: 'border-warning/50 bg-warning/10',
    high: 'border-destructive/50 bg-destructive/10',
  };

  const urgencyTextStyles = {
    low: 'text-muted-foreground',
    medium: 'text-warning',
    high: 'text-destructive',
  };

  return (
    <Alert className={cn(urgencyStyles[urgencyLevel], className)}>
      <Clock className={cn('h-4 w-4', urgencyTextStyles[urgencyLevel])} />
      <AlertTitle className={cn('flex items-center justify-between', urgencyTextStyles[urgencyLevel])}>
        <span>Auto-close scheduled</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 -mr-2"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm text-muted-foreground">
          This ticket will automatically close in <span className={cn('font-medium', urgencyTextStyles[urgencyLevel])}>{timeRemaining}</span> if no activity occurs.
        </p>
        
        <Progress value={progress} className="h-1.5" />
        
        <div className="flex items-center gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onExtend}
            className="gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Extend 7 days
          </Button>
          <span className="text-xs text-muted-foreground">
            Any activity will also reset the timer
          </span>
        </div>
      </AlertDescription>
    </Alert>
  );
}

// Configuration component for auto-close settings
interface AutoCloseSettingsProps {
  enabled: boolean;
  days: number;
  onEnabledChange: (enabled: boolean) => void;
  onDaysChange: (days: number) => void;
}

export function AutoCloseSettings({
  enabled,
  days,
  onEnabledChange,
  onDaysChange,
}: AutoCloseSettingsProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <h4 className="text-sm font-medium">Auto-close resolved tickets</h4>
          <p className="text-xs text-muted-foreground">
            Automatically close resolved tickets after a period of inactivity
          </p>
        </div>
        <Button
          variant={enabled ? 'default' : 'outline'}
          size="sm"
          onClick={() => onEnabledChange(!enabled)}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </Button>
      </div>

      {enabled && (
        <div className="flex items-center gap-3 pl-4 border-l-2 border-muted">
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Close after</span>
          <select
            value={days}
            onChange={(e) => onDaysChange(Number(e.target.value))}
            className="px-2 py-1 text-sm border rounded-md bg-background"
          >
            <option value={3}>3 days</option>
            <option value={5}>5 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
          <span className="text-sm text-muted-foreground">of inactivity</span>
        </div>
      )}
    </div>
  );
}
